/**
 * 3D model loader core (EAA-1642).
 *
 * The "FE core handle" for loading a protected 3D model:
 *   1. Request a fresh one-time serve URL from the backend.
 *   2. Fetch the bytes (this consumes the single-use token).
 *   3. Parse them with three.js GLTFLoader (dynamically imported so the addon
 *      loader code-splits out of the main bundle).
 *
 * Because the token is single-use, a failed fetch/parse cannot reuse the URL —
 * we transparently re-request a fresh token and retry once. v1 supports
 * self-contained GLB / embedded glTF (a single fetch); multi-file glTF with
 * external .bin/textures is a follow-up (would need a token per sub-resource).
 */

import type { Group, AnimationClip } from 'three';
import { requestModel3DToken } from '../api';

export interface LoadedModel {
    scene: Group;
    /** Animation clips embedded in the GLB/glTF file (may be empty). */
    animations: AnimationClip[];
    /** Revoke object URLs / dispose helpers created during load. */
    dispose: () => void;
}

async function fetchModelBytes(assetId: string): Promise<ArrayBuffer> {
    const { url } = await requestModel3DToken(assetId);
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) {
        throw new Error(`Failed to fetch model (HTTP ${res.status})`);
    }
    return res.arrayBuffer();
}

interface ParsedGltf {
    scene: Group;
    animations: AnimationClip[];
}

async function parseGltf(bytes: ArrayBuffer): Promise<ParsedGltf> {
    // Dynamic import → GLTFLoader lands in its own chunk, not the main bundle.
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();
    return new Promise<ParsedGltf>((resolve, reject) => {
        loader.parse(
            bytes,
            '',
            (gltf) => resolve({ scene: gltf.scene, animations: gltf.animations ?? [] }),
            (err) => reject(err instanceof Error ? err : new Error('Failed to parse 3D model')),
        );
    });
}

/**
 * Load a model asset, retrying once with a fresh one-time token.
 */
export async function loadModel3D(assetId: string): Promise<LoadedModel> {
    if (!assetId) {
        throw new Error('Missing 3D model asset id.');
    }

    let lastErr: unknown;
    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const bytes = await fetchModelBytes(assetId);
            const { scene, animations } = await parseGltf(bytes);
            return { scene, animations, dispose: () => disposeObject3D(scene) };
        } catch (err) {
            lastErr = err;
        }
    }
    throw lastErr instanceof Error ? lastErr : new Error('Could not load the 3D model.');
}

/** Free GPU resources for a loaded scene graph. */
export function disposeObject3D(root: { traverse: (cb: (o: unknown) => void) => void }): void {
    root.traverse((obj: unknown) => {
        const mesh = obj as {
            geometry?: { dispose?: () => void };
            material?: unknown;
        };
        mesh.geometry?.dispose?.();
        const material = mesh.material;
        if (Array.isArray(material)) {
            material.forEach((m) => (m as { dispose?: () => void })?.dispose?.());
        } else {
            (material as { dispose?: () => void })?.dispose?.();
        }
    });
}

